/**
 * LockedFeaturePage — Phase 30 Wave 1 (30.1.2)
 *
 * Single shared component rendering the upgrade prompt for in-flight
 * surfaces (Command Center, Machine Fleet). Accepts copy + icon as props so each
 * route can hint at the future surface it represents.
 *
 * Behavior: always renders. Does not redirect or 403. The nav-item's
 * `locked: true` flag is what shows the lock affordance in the sidebar;
 * a deep-link to the URL always lands here regardless of role or flag.
 */

import { Lock, Sparkles, ArrowRight } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { useDocumentTitle } from '../lib/hooks/useDocumentTitle';

interface LockedFeaturePageProps {
  /** Hero title for the locked feature (e.g., "Your consolidated command center"). */
  title: string;
  /** Short summary of what the surface is. */
  description: string;
  /** Longer hint paragraph describing what to expect once the feature ships. */
  hint: string;
  /** Page-specific icon shown in the hero band alongside the lock overlay. */
  icon: LucideIcon;
  /** Display label used in the doc title. Per n2: pass with `(Coming Soon)` suffix
   * so a bookmarked tab clearly shows that the user landed on a placeholder. */
  pageTitle: string;
}

export function LockedFeaturePage({
  title,
  description,
  hint,
  icon: Icon,
  pageTitle,
}: LockedFeaturePageProps) {
  useDocumentTitle(pageTitle);

  return (
    <div className="max-w-2xl mx-auto py-12">
      <div className="rounded-3xl border border-guild-mint/40 bg-gradient-to-br from-white via-guild-mint/5 to-white shadow-lg shadow-guild-green/5 overflow-hidden">
        {/* Hero band */}
        <div className="px-8 py-10 border-b border-guild-mint/30 bg-gradient-to-r from-guild-green/5 to-transparent">
          <div className="flex items-start gap-5">
            <div className="relative flex-shrink-0">
              <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-br from-guild-green to-guild-green/80 shadow-lg shadow-guild-green/25">
                <Icon size={28} strokeWidth={2.5} className="text-white" />
              </div>
              <div className="absolute -bottom-1 -right-1 flex h-7 w-7 items-center justify-center rounded-full bg-amber-400 ring-2 ring-white">
                <Lock size={12} strokeWidth={3} className="text-amber-900" />
              </div>
            </div>
            <div className="space-y-2 flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <Sparkles size={14} className="text-guild-green" />
                <span className="text-xs font-bold text-guild-green uppercase tracking-[0.15em]">
                  Coming soon
                </span>
              </div>
              <h1 className="text-2xl font-bold tracking-tight text-gray-900">{title}</h1>
              <p className="text-sm text-gray-600 leading-relaxed">{description}</p>
            </div>
          </div>
        </div>

        {/* Hint section */}
        <div className="px-8 py-6 space-y-4">
          <div>
            <p className="text-[11px] font-bold text-gray-400 uppercase tracking-[0.15em] mb-2">
              What you can expect
            </p>
            <p className="text-sm text-gray-700 leading-relaxed">{hint}</p>
          </div>

          {/* CTA — disabled placeholder. The user is the platform owner; no live
              upsell flow. The title attribute hints at the future routing. */}
          <button
            type="button"
            disabled
            className="
              inline-flex items-center gap-2 rounded-xl
              bg-gray-100 px-5 py-2.5
              text-sm font-semibold text-gray-400
              cursor-not-allowed
            "
            title="This feature is locked. Contact your account team to upgrade."
          >
            Upgrade to unlock
            <ArrowRight size={16} strokeWidth={2.5} />
          </button>
          <p className="text-xs text-gray-400">
            Contact your account team to learn more about availability and pricing.
          </p>
        </div>
      </div>
    </div>
  );
}

export default LockedFeaturePage;
